/**
 * Form Parser Helper
 *
 * Special parser for form components that creates field children
 * and maps them to table column IDs.
 */

import { v4 as uuidv4 } from 'uuid'

import type { IBody, IBodySettings } from '@makyoapp/makyo-sdk/models/AppBuilder.js'

import {
  CheckboxComp,
  DateTimePickerComp,
  FieldRatingComp,
  FileComp,
  HiddenFieldComp,
  IconPickerComp,
  ImageUploadComp,
  PhoneInputComp,
  SelectionComp,
  SliderComp,
  SwitchComp,
  TextFieldComp,
} from '../../form'
import type { AiComponentInput } from '../../registry'
import { extractTextFromVariableText, parseCornerRadiusFormat, parseToVariableText } from '../../parser-utils'
import type { AiFormInput } from './form.comp'
import { FormComp } from './form.comp'

/**
 * Form Field Parser Registry
 * Maps field types to their parser instances
 *
 * To add a new field parser:
 * 1. Create the field component parser (e.g., SelectionComp)
 * 2. Import it at the top of this file
 * 3. Add an entry here: 'selection': new SelectionComp()
 *
 * If a field type is not registered, the generic fallback parser will be used.
 */
const FIELD_PARSER_REGISTRY: Record<string, any> = {
  'text-field': new TextFieldComp(),
  selection: new SelectionComp(),
  'datetime-picker': new DateTimePickerComp(),
  switch: new SwitchComp(),
  slider: new SliderComp(),
  'phone-input': new PhoneInputComp(),
  'image-upload': new ImageUploadComp(),
  file: new FileComp(),
  'field-rating': new FieldRatingComp(),
  checkbox: new CheckboxComp(),
  'hidden-field': new HiddenFieldComp(),
  'icon-picker': new IconPickerComp(),
  // Add more field parsers as needed
}

/**
 * Map component types to their group IDs
 */
const COMPONENT_GROUP_MAP: Record<string, string> = {
  // Form field components
  'text-field': 'form',
  selection: 'form',
  checkbox: 'form',
  switch: 'form',
  slider: 'form',
  'datetime-picker': 'form',
  file: 'form',
  'image-upload': 'form',
  'phone-input': 'form',
  'field-rating': 'form',
  'multi-selection': 'form',
  'hidden-field': 'form',
  'icon-picker': 'form',
  // Other components
  button: 'button',
  section: 'container',
  text: 'text',
  'rich-text': 'text',
}

/**
 * Generic fallback parser for form fields without dedicated parsers
 */
function parseFormFieldGeneric(
  child: AiComponentInput & { columnId?: string; label?: string; [key: string]: any },
  fieldType: string
): any {
  const settings: any = {}

  // Common field settings
  if (child.label) {
    settings[`${fieldType}-data-label`] = parseToVariableText(child.label)
  }

  // Map the field to the column ID (this is form-specific)
  if (child.columnId) {
    settings[`${fieldType}-data-value`] = child.columnId
  }

  // Default value
  if (child.defaultValue !== undefined) {
    settings[`${fieldType}-data-default-value`] = {
      flag: 2048, // CustomText flag
      id: uuidv4(),
      value: child.defaultValue,
    }
  }

  // Required field
  if (child.required !== undefined) {
    settings[`${fieldType}-behavior-required`] = child.required
  }

  // Help text
  if (child.helpText) {
    settings[`${fieldType}-data-help`] = parseToVariableText(child.helpText)
  }

  // Placeholder
  if (child.placeholder) {
    settings[`${fieldType}-data-placeholder`] = parseToVariableText(child.placeholder)
  }

  return settings
}

/**
 * Parse a form field and create its settings
 * Uses the appropriate field component parser from the registry
 */
function parseFormFieldSettings(
  child: AiComponentInput & { columnId?: string; label?: string; [key: string]: any },
  tableId: string
): any {
  const fieldType = (child as any).type

  // Check if we have a registered parser for this field type
  const parser = FIELD_PARSER_REGISTRY[fieldType]
  if (parser && typeof parser.parse === 'function') {
    return parser.parse(child)
  }

  // Fallback to generic parsing for field types without dedicated parsers
  console.warn(`No dedicated parser found for field type: ${fieldType}. Using generic fallback.`)
  return parseFormFieldGeneric(child, fieldType)
}

/**
 * Create submit button settings
 */
function createSubmitButtonSettings(submitButton: AiFormInput['submitButton'], tableId: string): any {
  const settings: any = {
    'button-data-label': parseToVariableText(submitButton?.label || 'Submit'),
    'button-design-size': submitButton?.size || 'lg',
    'button-design-shape': submitButton?.cornerRadius ? parseCornerRadiusFormat(submitButton.cornerRadius).phone : 'medium',
    'button-design-full-width': {
      phone: true,
      'phone-landscape': false,
      tablet: false,
      'tablet-landscape': false,
      desktop: false,
      tv: false,
      overflowSetting: {
        phone: true,
        'phone-landscape': true,
        tablet: true,
        'tablet-landscape': true,
        desktop: true,
        tv: true,
      },
    },
  }

  // Background color
  if (submitButton?.backgroundColor) {
    // Parse color format if provided
    // For now, just use adaptive default
    settings['button-design-color-type'] = {
      light: 'adaptive',
      dark: 'adaptive',
    }
    settings['button-design-color-type-solid'] = {
      light: 'primary-20',
      dark: 'primary-20',
    }
  }

  // Default action: submit form to table
  settings['button-action-click'] = submitButton?.actions ?? [
    {
      id: uuidv4(),
      type: 'form_add-row',
      settings: {
        tableId: tableId,
        data: {},
      },
    },
    {
      id: uuidv4(),
      type: 'alert_show-snackbar',
      settings: {
        bgColor: 'primary-20',
        value: parseToVariableText('Form submitted successfully'),
      },
    },
    {
      id: uuidv4(),
      type: 'nav_close_overlay',
      settings: {},
    },
  ]

  return settings
}

/**
 * Create form structure with field children
 * This is the special parser function called from the main parser
 */
export function createFormStructure(
  input: AiFormInput & { type: string; title?: string; children?: AiComponentInput[] },
  bodySettings: IBodySettings,
  parseChildComponent: (child: AiComponentInput, settings: IBodySettings) => IBody
): IBody {
  // Generate form container ID
  const formId = uuidv4()

  // Parse form container settings
  const formComp = new FormComp()
  const formSettings = formComp.parse(input)
  bodySettings[formId] = formSettings

  // Create field bodies from children
  const fieldBodies: IBody[] = []

  // Recursively check if there's already a button child (which would be the submit button)
  // This includes buttons nested inside sections
  const findButtonRecursive = (children: AiComponentInput[] | undefined): boolean => {
    if (!children || children.length === 0) return false
    for (const child of children) {
      if (child.type === 'button') {
        return true
      }
      // Recursively check nested children (e.g., sections containing buttons)
      if (child.children && findButtonRecursive(child.children)) {
        return true
      }
    }
    return false
  }
  const hasButtonChild = findButtonRecursive(input.children)

  // Recursively mark buttons as submit buttons when inside form
  const markButtonsAsSubmit = (body: IBody): void => {
    if (body._ComponentID === 'button') {
      body._IsSubmit = true
    }
    // Recursively process nested children
    if (body._Body && body._Body.length > 0) {
      for (const nestedChild of body._Body) {
        markButtonsAsSubmit(nestedChild)
      }
    }
  }

  if (input.children && input.children.length > 0) {
    for (const child of input.children) {
      // Check if this is a form field component that needs special handling
      const groupId = COMPONENT_GROUP_MAP[child.type] || 'form'
      const isFormField = groupId === 'form'

      if (isFormField) {
        // Handle form fields with columnId mapping
        const fieldId = uuidv4()

        // Store field settings with columnId mapping
        const fieldSettings = parseFormFieldSettings(child as any, input.tableId)
        bodySettings[fieldId] = fieldSettings

        // Extract title text from label array or use title string
        const titleText = child.title || extractTextFromVariableText((child as any).label) || ''

        // Create field body
        const fieldBody: IBody = {
          _id: fieldId,
          _ComponentID: child.type,
          _GroupID: groupId,
          _Title: titleText,
          _Body: [],
          _Draggable: true,
          _Removable: true,
          _IsSubmit: false,
          _Actions: {},
          _Behavior: {
            _Metadata: {},
            _Visibility: {
              _Type: '',
              _Clauses: [],
            },
            _UserControlledFilter: {
              _Type: '',
              _FilterBy: '',
              _ShowOnPage: false,
              _ShowOnFilterPage: false,
              _TitleIcon: { value: '' },
              _AllowMultiple: false,
            },
          },
        }
        fieldBodies.push(fieldBody)
      } else if (child.type === 'button') {
        // Button child - parse it and mark as submit button
        const childBody = parseChildComponent(child, bodySettings)
        childBody._IsSubmit = true
        fieldBodies.push(childBody)
      } else {
        // For other non-field components (like sections, etc.), use the normal parser
        // This will recursively parse nested children
        const childBody = parseChildComponent(child, bodySettings)
        // Recursively mark any buttons inside as submit buttons
        markButtonsAsSubmit(childBody)
        fieldBodies.push(childBody)
      }
    }
  }

  // Add submit button only if:
  // 1. submitButton is not null (explicitly disabled)
  // 2. AND there's no button child already (to avoid duplicates)
  if (input.submitButton !== null && !hasButtonChild) {
    const buttonId = uuidv4()
    const buttonSettings = createSubmitButtonSettings(input.submitButton, input.tableId)
    bodySettings[buttonId] = buttonSettings

    const buttonBody: IBody = {
      _id: buttonId,
      _ComponentID: 'button',
      _GroupID: 'button',
      _Title: '',
      _Body: [],
      _Draggable: true,
      _Removable: true,
      _IsSubmit: true,
      _Actions: {},
      _Behavior: {
        _Metadata: {},
        _Visibility: {
          _Type: '',
          _Clauses: [],
        },
        _UserControlledFilter: {
          _Type: '',
          _FilterBy: '',
          _ShowOnPage: false,
          _ShowOnFilterPage: false,
          _TitleIcon: { value: '' },
          _AllowMultiple: false,
        },
      },
    }
    fieldBodies.push(buttonBody)
  }

  // Create form container body with field children (with _Type, _Data, _Design as in template)
  const formBody: IBody = {
    _id: formId,
    _ComponentID: 'form',
    _GroupID: 'container',
    _Title: input.title || '', // BaseAiComponentInput.title for component body _Title
    _Body: fieldBodies,
    _Draggable: true,
    _Removable: true,
    _IsSubmit: false,
    _Actions: {},
    _Behavior: {
      _Metadata: {},
      _Visibility: {
        _Type: '',
        _Clauses: [],
      },
      _UserControlledFilter: {
        _Type: '',
        _FilterBy: '',
        _ShowOnPage: false,
        _ShowOnFilterPage: false,
        _TitleIcon: { value: '' },
        _AllowMultiple: false,
      },
    },
  }

  return formBody
}
